import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";

import U2000BasicTable, { type U2000SearchPayload, type U2000SearchResult } from "../../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000IdColumnDef, type U2000TableColumn } from "../../u2000Core/components/tables/basic/U2000BasicTableList";
import React, { useEffect, useRef } from "react";
import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { useU2000SearchHandlers } from "../../u2000Core/hooks/handler/U2000UseSearchHandlers";
import { useU2000QuickSearchListEffect } from "../../u2000Core/hooks/useU2000QuickSearchListEffect";
import { useNavigate } from "react-router-dom";
import type { TFunction } from "i18next";

import type { U2000TableHandlers } from "../../u2000Core/types/U2000TableHandlers";
import { U2000GridStateEnumPaginationDirection } from "../../u2000Core/reducer/formReducer/U2000GridState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import { useU2000DeleteHandlers } from "../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { U2000GridStateDeleteAction, type U2000GridStateNextPageAfterDeleteReturn } from "../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { useU3106Context } from "../U3106Context";
import { doDeleteData, doSearch, luHandleAppiError, type U3106ExpenseTypeSearchRow } from "../U3106Service";
import { NS } from "../U3106";
import U3106ExpenseTypeDeleteDlg from "./common/U3106ExpenseTypeDeleteDlg";
import { U3106ExpenseTypeListToolBar } from "./list/U3106ExpenseTypeListToolBar";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import { u2000BuildQuickSearchFormPath, u2000ShouldQuickSearchOpenForm } from "../../u2000Core/tools/U2000QuickSearchUtils";
import { formatCurrency } from "../../u2000Core/tools/U2000CurrencyUtils";

const CURRENCY_DISPLAY_OPTS = {
  currencySymbol: "$",
  symbolPosition: "left" as const,
  decimalSeparator: "." as const,
  thousandSeparator: " " as const,
  decimalDigits: 2,
};

const DECIMAL_DISPLAY_OPTS = {
  currencySymbol: "",
  symbolPosition: "left" as const,
  decimalSeparator: "." as const,
  thousandSeparator: " " as const,
  decimalDigits: 2,
};

/** Valeur search/API : nombre, chaîne, ou enveloppe JSON (ex. { value }). */
function parseNumericGridValue(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return null;
    const n = Number(trimmed.replace(",", "."));
    return Number.isFinite(n) ? n : null;
  }
  if (typeof value === "object" && value !== null) {
    const o = value as Record<string, unknown>;
    if ("value" in o) return parseNumericGridValue(o.value);
  }
  return null;
}

function parseStatusGridValue(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "number" && Number.isFinite(value)) return Math.trunc(value);
  if (typeof value === "object" && value !== null) {
    const o = value as Record<string, unknown>;
    if (typeof o.value === "number" && Number.isFinite(o.value)) return Math.trunc(o.value);
    if (typeof o.code === "number" && Number.isFinite(o.code)) return Math.trunc(o.code);
  }
  return null;
}

function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: "code",
      label: t("lblCode", { ns: NS }),
      widthClass: "w-[160px]",
      type: "string",
      align: "left",
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: true,
    },
    {
      name: "description",
      label: t("lblDescription", { ns: NS }),
      widthClass: "w-[280px]",
      type: "string",
      align: "left",
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
    },
    {
      name: "factor",
      label: t("lblFactor", { ns: NS }),
      widthClass: "w-[120px]",
      type: "number",
      align: "right",
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
      display: (_column, value) => {
        const n = parseNumericGridValue(value);
        if (n === null) return null;
        return formatCurrency(n, CURRENCY_DISPLAY_OPTS);
      },
    },
    {
      name: "majorFactor",
      label: t("lblMajorFactor", { ns: NS }),
      widthClass: "w-[130px]",
      type: "number",
      align: "right",
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
      display: (_column, value) => {
        const n = parseNumericGridValue(value);
        if (n === null) return null;
        return formatCurrency(n, DECIMAL_DISPLAY_OPTS);
      },
    },
    {
      name: "status",
      label: t("lblStatus", { ns: NS }),
      widthClass: "w-[120px]",
      type: "number",
      align: "center",
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
      display: (_column, value) => {
        const s = parseStatusGridValue(value);
        if (s === 1) return t("lblStatusActive", { ns: NS });
        if (s === 0) return t("lblStatusInactive", { ns: NS });
        if (s === null) return null;
        return t("lblStatusOther", { ns: NS, value: s });
      },
    },
    {
      name: "createdAt",
      label: t("lblFldCreatedAt", { ns: NS }),
      widthClass: "w-[150px]",
      isDefaultColumn: true,
      type: "date",
      inGridField: true,
      sortable: true,
      includeConditions: ["equals", "notequals", "greaterthan", "greaterthanorequal", "lesthan", "lesthanorequal", "interval"],
      isCriteriaField: true,
    },
    {
      name: "deletedAt",
      label: t("lblCndFieldDeletedAt", { ns: NS }),
      widthClass: "w-[250px]",
      type: "date",
      inGridField: true,
      align: "center",
      display: (_column, value) => {
        return value ? (
          <span className="font-bold text-red-600">{t("lblRowSoftDeleted", { ns: NS })}</span>
        ) : (
          ""
        );
      },
      includeConditions: ["isNotNull"],
      ovverideLabelConditions: [
        {
          name: "isNotNull",
          label: t("lblCndDeletedAt", { ns: NS }),
        },
      ],
      isCriteriaField: true,
    },
  ];
}

const U3106ExpenseTypeList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();

  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3106Context();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { setLogicalUnitBreadcrumb, quickSearchData, setQuickSearchData } = useU2000LogicalUnitContext();
  const consumeQsSessionRef = useRef<() => { isFromQuickSearch: boolean; targetLogicalUnit: string } | null>(() => null);

  // Dépendances volontairement limitées à `t` : les handlers grille/recherche
  // ne sont pas stables (nouvel objet à chaque rendu) — les inclure provoquait une boucle infinie
  // (Maximum update depth exceeded). Même modèle que U3101EmployeeList ([t] seul pour l’init colonnes).
  useEffect(() => {
    const listTitle = t("title", { ns: NS });
    searchStateHandlers.setColumns(getColumnsDef(t));
    gridStateHandlers.setColumns(getColumnsDef(t));
    setLogicalUnitBreadcrumb([{ label: listTitle }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- searchStateHandlers / gridStateHandlers instables
  }, [t]);

  const handleSearch = useU2000SearchHandlers<U3106ExpenseTypeSearchRow>({
    doSearchFn: async (searchPayload: U2000SearchPayload) => {
      return await doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<U3106ExpenseTypeSearchRow>, direction: U2000GridStateEnumPaginationDirection) => {
      gridStateHandlers.initGridAfterSearch(searchResult, direction);
      searchStateHandlers.keepFilterAfterSearch(searchStateHandlers.extractSearchParameter());

      const qsSession = consumeQsSessionRef.current();
      const isFromQuickSearch = qsSession?.isFromQuickSearch === true;
      const targetLU = qsSession?.targetLogicalUnit ?? "/U3106";
      if (
        u2000ShouldQuickSearchOpenForm(window.location.pathname, targetLU) &&
        searchResult.data &&
        searchResult.data.length === 1 &&
        isFromQuickSearch
      ) {
        const single = searchResult.data[0];
        luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
        luStateHandlers.setActionOriginFrom("");
        const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === single.id);
        if (currentIdx !== -1) {
          gridStateHandlers.setCurrentIdx(currentIdx);
        }
        navigate(u2000BuildQuickSearchFormPath(targetLU, single.id), { replace: true });
      }
    },
    onError: ({ error }: { error: unknown }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- erreur axios
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => (gridStateHandlers ? gridStateHandlers.getSelectedRows() : []),
    doDeleteFn: async (id, row) => {
      return await doDeleteData(u2000AxiosAuthenticated, id, { id: row.id, version: row.version });
    },

    onSuccess: (deletedRows) => {
      const page = gridStateHandlers.getCurrentPage();
      let newData = gridStateHandlers.getData();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        toast.success(t("success0003", { key: row.code ?? row.id, ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      const action: U2000GridStateNextPageAfterDeleteReturn = gridStateHandlers.getNextPageAfterDelete(page, totalRows);
      if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.NEXTPAGE);
      }
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  const u3106ListHandlerActions: U2000TableHandlers<U3106ExpenseTypeSearchRow> = {
    onSearch: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onRowClick: (row: U3106ExpenseTypeSearchRow) => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
      luStateHandlers.setActionOriginFrom("");
      const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === row.id);
      gridStateHandlers.setCurrentIdx(currentIdx);
      navigate(`${row.id}`);
    },
    onAdd: () => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.ADD);
      luStateHandlers.setActionOriginFrom("/U3106");
      navigate("new");
    },
    onModify: () => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.MODIFY);
      luStateHandlers.setActionOriginFrom("/U3106");
      navigate(`${gridStateHandlers.getSelectedRows()[0]?.id}`);
    },
    onDelete: () => {
      handleDelete.handleDelete();
    },
  };

  const { consumeQuickSearchSession } = useU2000QuickSearchListEffect({
    targetLogicalUnit: "/U3106",
    t,
    searchStateHandlers,
    gridStateHandlers,
    runSearch: handleSearch.handleSearch,
    quickSearchData,
    setQuickSearchData,
  });
  consumeQsSessionRef.current = consumeQuickSearchSession;

  if (searchStateHandlers.getColumns().length === 0) {
    return null;
  }

  return (
    <>
      <U3106ExpenseTypeDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
      <U2000BasicTable
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        actionsButtons={
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- toolbar identique U3101
          <U3106ExpenseTypeListToolBar gridStateHandlers={gridStateHandlers as any} handlerActions={u3106ListHandlerActions as any} />
        }
        handlerActions={u3106ListHandlerActions}
        label={t("lblExpenseTypeList", {
          count: gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
        allowRowSelection={true}
        hasExportRole={false}
      />
    </>
  );
};

export default U3106ExpenseTypeList;
